// ------------------------------
// MUST COME FIRST. Sentry instrumentation setup for react-native (but not API
// routes).
// ------------------------------
import { routingIntegration } from "@/client/sentry";

// ------------------------------
// Continue on with the rest of the file as normal. It's important that
// `Sentry.init()` comes first as it hooks into require()/import calls so it
// needs to be very early on in the setup.
// ------------------------------

import { useAuth } from "@/client/auth";
import { AudioContextProvider } from "@/client/ui/AudioContextProvider";
import { DebuggerHotkey } from "@/client/ui/DebuggerHotkey";
import { DeviceStoreProvider } from "@/client/ui/DeviceStoreProvider";
import { KeyboardShortcutProvider } from "@/client/ui/KeyboardShortcutProvider";
import { PostHogProvider } from "@/client/ui/PostHogProvider";
import { PylyThemeProvider } from "@/client/ui/PylyThemeProvider";
import { SessionStoreProvider } from "@/client/ui/SessionStoreProvider";
import { SplashScreen } from "@/client/ui/SplashScreen";
import { Suspense } from "@/client/ui/Suspense";
import { TooltipProvider } from "@/client/ui/TooltipProvider";
import * as Sentry from "@sentry/react-native";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { Stack, useNavigationContainerRef } from "expo-router";
import Head from "expo-router/head";
import { PortalProvider } from "react-native-teleport";
import type { PropsWithChildren } from "react";
import { useEffect } from "react";

function RootLayout() {
  // Capture the NavigationContainer ref and register it with the instrumentation.
  const ref = useNavigationContainerRef();

  useEffect(() => {
    routingIntegration.registerNavigationContainer(ref);
  }, [ref]);

  return (
    <DeviceStoreProvider>
      <Suspense fallback={null}>
        <PylyThemeProvider>
          <CurrentSessionStoreProvider>
            <PostHogProvider>
              <Head>
                <title>Pinyinly - Learn to read Chinese</title>
              </Head>

              <UiProviders>
                <Stack
                  screenOptions={{ headerShown: false, animation: `fade` }}
                >
                  <Stack.Screen
                    name="login"
                    options={{
                      presentation: `modal`,
                      animation: `slide_from_bottom`,
                    }}
                  />
                </Stack>
              </UiProviders>
              <SplashScreen />
            </PostHogProvider>
          </CurrentSessionStoreProvider>
        </PylyThemeProvider>
      </Suspense>
    </DeviceStoreProvider>
  );
}

export function UiProviders({ children }: PropsWithChildren) {
  return (
    <PortalProvider>
      <AudioContextProvider>
        <KeyboardShortcutProvider>
          <DebuggerHotkey />
          <TooltipProvider>{children}</TooltipProvider>
        </KeyboardShortcutProvider>
      </AudioContextProvider>
    </PortalProvider>
  );
}

function CurrentSessionStoreProvider({ children }: PropsWithChildren) {
  const activeDeviceSession = useAuth().data?.activeDeviceSession;
  const userId = activeDeviceSession?.userId;

  useEffect(() => {
    Sentry.setUser(userId == null ? null : { id: userId });
  }, [userId]);

  return activeDeviceSession == null ? null : (
    <SessionStoreProvider
      dbName={activeDeviceSession.replicacheDbName}
      serverSessionId={activeDeviceSession.serverSessionId}
    >
      <ReactQueryDevtools initialIsOpen />
      {children}
    </SessionStoreProvider>
  );
}

// Wrap the Root Layout route component with `Sentry.wrap` to capture gesture info and profiling data.
// oxlint-disable-next-line react/only-export-components
export default Sentry.wrap(RootLayout);
